import React, { forwardRef } from 'react';
import clsx from 'clsx';

export type BadgeVariant =
  | 'blue'
  | 'fuchsia'
  | 'cyan'
  | 'success'
  | 'warning'
  | 'error'
  | 'neutral';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Color variant */
  variant?: BadgeVariant;
  /** Badge size */
  size?: 'sm' | 'md';
  /** Show a small colored dot before the text */
  dot?: boolean;
}

const variantClasses: Record<BadgeVariant, { bg: string; text: string; dot: string }> = {
  blue: {
    bg: 'bg-[var(--volt-blue-muted)]',
    text: 'text-[var(--volt-blue)]',
    dot: 'bg-[var(--volt-blue)]',
  },
  fuchsia: {
    bg: 'bg-[var(--volt-fuchsia-muted)]',
    text: 'text-[var(--volt-fuchsia)]',
    dot: 'bg-[var(--volt-fuchsia)]',
  },
  cyan: {
    bg: 'bg-[var(--volt-cyan-muted)]',
    text: 'text-[var(--volt-cyan)]',
    dot: 'bg-[var(--volt-cyan)]',
  },
  success: {
    bg: 'bg-[var(--volt-success-muted)]',
    text: 'text-[var(--volt-success)]',
    dot: 'bg-[var(--volt-success)]',
  },
  warning: {
    bg: 'bg-[var(--volt-warning-muted)]',
    text: 'text-[var(--volt-warning)]',
    dot: 'bg-[var(--volt-warning)]',
  },
  error: {
    bg: 'bg-[var(--volt-error-muted)]',
    text: 'text-[var(--volt-error)]',
    dot: 'bg-[var(--volt-error)]',
  },
  neutral: {
    bg: 'bg-[var(--volt-dark-elevated)]',
    text: 'text-[var(--volt-text-secondary)]',
    dot: 'bg-[var(--volt-text-muted)]',
  },
};

const sizeClasses: Record<NonNullable<BadgeProps['size']>, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-xs',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ variant = 'blue', size = 'md', dot = false, className, children, ...props }, ref) => {
    const colors = variantClasses[variant];

    return (
      <span
        ref={ref}
        className={clsx(
          'inline-flex items-center gap-1.5 font-medium rounded-full',
          colors.bg,
          colors.text,
          sizeClasses[size],
          className,
        )}
        {...props}
      >
        {dot && (
          <span
            className={clsx('h-2 w-2 rounded-full flex-shrink-0', colors.dot)}
            aria-hidden="true"
          />
        )}
        {children}
      </span>
    );
  },
);

Badge.displayName = 'Badge';
